import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getUserId} from "../../utils/common"
import httpService from "../../utils/httpService"
import {setToastr} from "../App/appSlice"

export type Dealer = {
    dealerId: number
    regionId: number
    dealerNumber: string
    dealerName: string
    preparerId: string
    active: boolean
    hasLocalATF: boolean
    selected: boolean
    phoneNumber: string
}

export const initDealer = () =>
    ({
        dealerId: 0,
        regionId: 0,
        dealerNumber: "",
        dealerName: "",
        preparerId: "",
        active: true,
        hasLocalATF: false,
        phoneNumber: "",
    } as Dealer)

export type Region = {
    regionId: number
    regionName: string
    active: boolean
}

type RADState = {
    regions: Region[]
    dealers: Dealer[]
    selectedDealer: Dealer
    isRegionLoading: boolean
    fetchRegionError: string
    isDealerLoading: boolean
    fetchDealerError: string
    isLoading: boolean
    updatePreparerError: string
    selectedRegion: string
    regionSelection: {
        [id: string]: boolean | number[]
    }
    modalToggle: boolean
    regionSelected: {regionId: number; regionName: string}
}

const initialState: RADState = {
    regions: [],
    dealers: [],
    selectedDealer: {} as Dealer,
    isRegionLoading: false,
    fetchRegionError: "",
    isDealerLoading: false,
    fetchDealerError: "",
    isLoading: false,
    updatePreparerError: "",
    selectedRegion: "",
    regionSelection: {},
    modalToggle: false,
    regionSelected: {regionId: 0, regionName: ""},
}

const RADSlice = createSlice({
    name: "regionAndDealers",
    initialState,
    reducers: {
        showRegionLoader(state, action: PayloadAction<boolean>) {
            state.isRegionLoading = action.payload
        },
        setRegions(state, action: PayloadAction<any[]>) {
            state.regions = action.payload
        },
        setRegionFetchError(state, action: PayloadAction<string>) {
            state.fetchRegionError = action.payload
        },
        showDealerLoader(state, action: PayloadAction<boolean>) {
            state.isDealerLoading = action.payload
        },
        setDealers(state, action: PayloadAction<Dealer[]>) {
            state.dealers = action.payload
        },
        setSelectedDealer(state, action: PayloadAction<Dealer>) {
            state.selectedDealer = action.payload
        },
        setDealerFetchError(state, action: PayloadAction<string>) {
            state.fetchDealerError = action.payload
        },
        setLoader(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload
        },
        updatePreparer(state, action: PayloadAction<Dealer[]>) {
            state.dealers = action.payload
        },
        updatePreparerError(state, action: PayloadAction<string>) {
            state.updatePreparerError = action.payload
        },
        setRegionSelection(
            state,
            action: PayloadAction<{
                [id: string]: boolean | number[]
            }>,
        ) {
            state.regionSelection = action.payload
        },
        setSelectedRegion(state, action: PayloadAction<string>) {
            state.selectedRegion = action.payload
        },
        setModalToggle(state, action: PayloadAction<boolean>) {
            state.modalToggle = action.payload
        },
        setRegion(state, action: PayloadAction<{regionId: number; regionName: string}>) {
            state.regionSelected = action.payload
        },
    },
})

export const {
    showRegionLoader,
    setRegions,
    setRegionFetchError,
    showDealerLoader,
    setDealers,
    setDealerFetchError,
    setLoader,
    updatePreparer,
    updatePreparerError,
    setSelectedRegion,
    setRegionSelection,
    setModalToggle,
    setSelectedDealer,
    setRegion,
} = RADSlice.actions

export default RADSlice.reducer

export const fetchRegions = (): AppThunk => (dispatch: any, getState) => {
    dispatch(showRegionLoader(true))
    httpService(getAccessToken(getState()))
        .get(`users/${getUserId(getState())}/regions`)
        .then(response => dispatch(setRegions(response.data)))
        .catch(error => dispatch(setRegionFetchError(error.response.data.Value.Error)))
        .finally(() => dispatch(showRegionLoader(false)))
}

export const fetchDealers =
    (regionId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(showDealerLoader(true))
        httpService(getAccessToken(getState()))
            .get(`users/${getUserId(getState())}/regions/${regionId}/dealers`)
            .then(response => dispatch(setDealers(response.data)))
            .catch(error => dispatch(setDealerFetchError(error.response.data.Value.Error)))
            .finally(() => dispatch(showDealerLoader(false)))
    }

export const updatePreparerId =
    (dealerData: Dealer): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setLoader(true))
        httpService(getAccessToken(getState()))
            .put(`users/dealers/${dealerData.dealerId}`, dealerData)
            .then(response => {
                dispatch(fetchDealers(dealerData.regionId))
            })
            .catch(error => dispatch(updatePreparerError(error.response.data.Value.Error)))
            .finally(() => {
                dispatch(setModalToggle(false))
                dispatch(setSelectedDealer(initDealer()))
                dispatch(setLoader(false))
            })
    }

export const addDealer =
    (regionId: number, newDealer: any): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setLoader(true))
        httpService(getAccessToken(getState()))
            .post(`users/${getUserId(getState())}/regions/${regionId}/dealer`, newDealer)
            .then(() => {
                dispatch(fetchDealers(regionId))
                dispatch(setModalToggle(false))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Dealer added successfully, please refresh to see the latest information",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
            .finally(() => {
                dispatch(setSelectedDealer(initDealer()))
                dispatch(setLoader(false))
            })
    }

export const updateDealerStatus =
    (regionId: any, dealerId: Dealer[], active: Dealer[]): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setLoader(true))
        httpService(getAccessToken(getState()))
            .put(
                `users/${getUserId(getState())}/regions/${regionId}/dealer/${dealerId}/status?active=${
                    active ? false : true
                }`,
            )
            .then((res: {data: boolean}) => {
                const dealer = {...getState().containers.regions.selectedDealer}
                dealer.active = res.data
                dispatch(setSelectedDealer(dealer))
                dispatch(fetchDealers(regionId))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: `Dealer ${
                            active ? "deactivated" : "activated"
                        }, Please refresh to see the latest information`,
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
            .finally(() => dispatch(setLoader(false)))
    }
